import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { useDebounce } from 'use-debounce';

import RowItem from 'src/components/Admin/leaderboard/table/RowItem';
import TableHeader from 'src/components/Admin/leaderboard/table/TableHeader';
import { formatDateYearTime } from 'src/components/Crates';
import SpinnerLoading from 'src/components/loadings/SpinnerLoading';
import moderateService from 'src/services/adminDashboard/moderateService';
import Input from 'src/ui/Input';
import Pagination from 'src/ui/Pagination';

import 'src/assets/CSS/Admin.scss';

const HEADER_LIST = [
  {
    id: 0,
    title: 'Name',
    column: 'accountName',
    isSortable: true,
  },
  {
    id: 1,
    title: 'Shuffle Name',
    column: 'shuffleUsername',
    isSortable: true,
  },

  {
    id: 2,
    title: 'Discord Name',
    column: 'discordUsername',
    isSortable: true,
  },

  {
    id: 3,
    title: 'Authority',
    column: 'authority',
    isSortable: true,
  },
  {
    id: 4,
    title: 'Last Online',
    column: 'lastSeen',
    isSortable: true,
  },
];

const PendingTransactions = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('transactionPage'));
  const sortColumn = searchParams.get('transactionSortColumn');
  const sortOrder = searchParams.get('transactionSortOrder') || 'asc';
  const itemsPerPage = 10;

  const [search, setSearch] = useState('');
  const [searchUserName] = useDebounce(search, 1000);

  const {
    data: transactions = { data: [], total: 0 },
    isLoading: isLoadingTransactions,
  } = useQuery({
    queryKey: [
      'pending-transactions',
      { page, searchUserName, sortColumn, sortOrder },
    ],
    queryFn: () =>
      moderateService.getPendingTransactions({
        page: page - 1,
        name: searchUserName,
        size: itemsPerPage,
        sortColumn,
        sortOrder,
      }),
    placeholderData: (previousData) => previousData,
  });

  useEffect(() => {
    if (searchUserName !== '') {
      setSearchParams((prev) => {
        prev.set('transactionPage', '1');
        return prev;
      });
    }

    if (searchUserName === '') {
      setSearchParams((prev) => {
        prev.set('transactionPage', '1');
        return prev;
      });
    }
  }, [searchUserName]);

  useEffect(() => {
    if (!searchParams.has('transactionPage')) {
      setSearchParams((prev) => {
        prev.set('transactionPage', '1');
        return prev;
      });
    }
  }, []);

  const handleChangePage = (value: number) => {
    setSearchParams((prev) => {
      prev.set('transactionPage', value.toString());
      return prev;
    });
  };

  return (
    <div className="Dashboard-Pending">
      <span>Pending Transactions</span>

      <div style={{ maxWidth: '350px' }}>
        <Input
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {isLoadingTransactions ? (
          <SpinnerLoading />
        ) : (
          <div
            className="Btcs-table"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              width: '100%',
            }}
          >
            <TableHeader
              sortColKey="transactionSortColumn"
              sortColOrder="transactionSortOrder"
              columns={HEADER_LIST}
            />

            {transactions.data.map((player) => {
              return (
                <Link to={`/admin/users/${player.id}`}>
                  <RowItem
                    item={player}
                    fields={[
                      {
                        key: 'accountName',
                        render: (value) => value,
                      },
                      {
                        key: 'shuffleUsername',
                        render: (value) => (value ? value : 'N/A'),
                      },
                      {
                        key: 'discordUsername',
                        render: (value) => (value ? value : 'N/A'),
                      },
                      {
                        key: 'authority',
                        render: (value) => value,
                      },
                      {
                        key: 'lastSeen',
                        render: (value) => formatDateYearTime(value),
                      },
                    ]}
                  />
                </Link>
              );
            })}
          </div>
        )}

        <div>
          {transactions && transactions.total > itemsPerPage && (
            <Pagination
              count={Math.ceil(transactions.total / itemsPerPage)}
              itemsPerPage={itemsPerPage}
              page={page}
              onChangePage={(e, value) => handleChangePage(value)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PendingTransactions;
